import { useRef } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useIsMobile } from "@/hooks/use-mobile";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RefreshCw, Sparkles } from "lucide-react";
import { formatReadwiseTimestamp, getReadwiseIntegrationMeta } from "@/lib/readwise";
import { HighlightCard } from "@/components/readwise/HighlightCard";
import { HighlightRow } from "@/components/readwise/HighlightRow";
import { HighlightFilters, type Density } from "@/components/readwise/HighlightFilters";
import { useRegisterPageShortcuts } from "@/hooks/usePageShortcuts";
import { ReadingFontToggle } from "@/components/reading/ReadingFontToggle";
import { AppSeo } from "@/components/AppSeo";
import { useReadwiseHighlights } from "@/components/readwise/useReadwiseHighlights";
import { useReadwiseKeyboardNav } from "@/components/readwise/useReadwiseKeyboardNav";

const Inner = () => {
  const { active, activePersonaId } = useWorkspace();
  const isMobile = useIsMobile();
  const data = useReadwiseHighlights({
    workspaceId: active?.id ?? null,
    activePersonaId,
  });

  useRegisterPageShortcuts("readwise-highlights", [
    {
      heading: "Highlights — compact view",
      items: [
        { keys: ["J", "↓"], label: "Move focus to next highlight" },
        { keys: ["K", "↑"], label: "Move focus to previous highlight" },
        { keys: ["Enter", "Space"], label: "Expand or collapse focused row" },
        { keys: ["E"], label: "Expand or collapse all rows on this page" },
      ],
    },
    {
      heading: "Highlights — page",
      items: [{ keys: ["/"], label: "Focus the search input" }],
    },
  ]);

  const effectiveDensity: Density = isMobile ? "compact" : data.density;

  const rowRefsRef = useRef<(HTMLButtonElement | null)[]>([]);
  rowRefsRef.current = [];
  const searchInputRef = useRef<HTMLInputElement>(null);

  useReadwiseKeyboardNav({
    effectiveDensity,
    pagedLength: data.paged.length,
    page: data.page,
    rowRefs: rowRefsRef,
    searchInputRef,
    allExpandedOnPage: data.allExpandedOnPage,
    expandAllOnPage: data.expandAllOnPage,
    collapseAllOnPage: data.collapseAllOnPage,
  });

  const integrationMeta = getReadwiseIntegrationMeta(data.integration);
  const importedCountLabel = `${data.rows.length.toLocaleString()} highlights imported from Readwise`;

  return (
    <>
      <AppSeo title="Readwise Highlights" />
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-semibold">Readwise Highlights</h1>
            <p className="text-sm text-muted-foreground">{importedCountLabel}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Generating images from highlights still counts toward your monthly tier limit.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ReadingFontToggle />
            <Button onClick={data.handleSync} disabled={data.syncing}>
              <RefreshCw className={`h-4 w-4 ${data.syncing ? "animate-spin" : ""}`} />
              {data.syncing ? "Syncing…" : "Sync now"}
            </Button>
          </div>
        </div>

        <Alert variant={integrationMeta.tone === "destructive" ? "destructive" : "default"}>
          <Sparkles className="h-4 w-4" />
          <AlertTitle>{integrationMeta.summary}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{integrationMeta.detail}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>Status: {integrationMeta.badgeLabel}</span>
              <span>Last sync: {formatReadwiseTimestamp(data.integration?.last_synced_at)}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/settings"><Button variant="outline" size="sm">Open Settings</Button></Link>
              {integrationMeta.needsReconnect ? <Link to="/settings"><Button size="sm">Reconnect Readwise</Button></Link> : null}
            </div>
          </AlertDescription>
        </Alert>

        <HighlightFilters
          ref={searchInputRef}
          search={data.search}
          onSearchChange={data.setSearch}
          bookFilter={data.bookFilter}
          onBookFilterChange={data.setBookFilter}
          tagFilter={data.tagFilter}
          onTagFilterChange={data.setTagFilter}
          sourceFilter={data.sourceFilter}
          onSourceFilterChange={data.setSourceFilter}
          books={data.books}
          favoriteTag={data.favoriteTag}
          favoriteCount={data.favoriteCount}
          otherTags={data.otherTags}
          sources={data.sources}
          density={data.density}
          onDensityChange={data.setDensity}
          showExpandControls={effectiveDensity === "compact" && data.paged.length > 0}
          allExpandedOnPage={data.allExpandedOnPage}
          onExpandAll={data.expandAllOnPage}
          onCollapseAll={data.collapseAllOnPage}
        />

        {(data.sourceFilter === "supplemental" || data.sourceFilter === "supplementals" || data.sourceFilter === "tweet" || data.sourceFilter === "tweets") && (
          <Alert>
            <Sparkles className="h-4 w-4" />
            <AlertTitle>Auto-purged after 7 days</AlertTitle>
            <AlertDescription>
              Supplementals and Tweets are removed after 7 days to keep your library focused on durable highlights. Re-sync from Settings to pull current ones back.
            </AlertDescription>
          </Alert>
        )}

        {data.loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : data.rows.length === 0 ? (
          <Card className="shadow-soft">
            <CardContent className="py-10 text-center space-y-3">
              <p className="text-muted-foreground">
                No Readwise highlights yet. Connect your account in Settings, then sync.
              </p>
              <Button asChild variant="outline">
                <a href="/settings">Go to Settings</a>
              </Button>
            </CardContent>
          </Card>
        ) : data.filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm">No highlights match your filters.</p>
        ) : (
          <>
            <div className={effectiveDensity === "cards" ? "space-y-3" : "space-y-2"}>
              {effectiveDensity === "cards"
                ? data.paged.map((r) => {
                    const key = `${r.book_title ?? ""}|||${r.book_author ?? ""}`;
                    return <HighlightCard key={r.id} r={r} libraryMatch={data.libraryMatches[key] ?? null} returnTo={data.readwiseReturnTo} />;
                  })
                : data.paged.map((r, idx) => (
                    <HighlightRow
                      key={r.id}
                      ref={(el) => {
                        rowRefsRef.current[idx] = el;
                      }}
                      r={r}
                      libraryMatch={data.libraryMatches[`${r.book_title ?? ""}|||${r.book_author ?? ""}`] ?? null}
                      returnTo={data.readwiseReturnTo}
                      expanded={data.expanded.has(r.id)}
                      onToggle={() => data.toggleExpanded(r.id)}
                    />
                  ))}
            </div>

            {data.totalPages > 1 && (
              <div className="flex items-center justify-between text-sm pt-2">
                <span className="text-muted-foreground">
                  Page {data.page + 1} of {data.totalPages} · {data.filtered.length} highlights
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => data.setPage((p) => Math.max(0, p - 1))}
                    disabled={data.page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => data.setPage((p) => Math.min(data.totalPages - 1, p + 1))}
                    disabled={data.page >= data.totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const ReadwiseHighlights = () => <Inner />;

export default ReadwiseHighlights;
